import { Select, Tooltip } from '@neo4j-ndl/react';
import { type Instance, type LOG_TYPE, TIER } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import React from 'react';

type Props = {
  instances: Instance[];
  selectedInstance: Instance | null;
  setSelectedInstance: (i: Instance) => void;
  disabledOption: (i: Instance) => boolean;
  logType: LOG_TYPE;
};

export const InstanceDropdown = ({
  instances,
  selectedInstance,
  setSelectedInstance,
  disabledOption,
  logType,
}: Props) => {
  const instanceOptions: { label: string; value: Instance; key: string }[] = instances
    .filter((i) => i.tier === TIER.MTE)
    .map((i) => ({
      label: i.name,
      value: i,
      key: i.id,
    }));

  return (
    <Select
      size="medium"
      label="Instance"
      type="select"
      selectProps={{
        options: instanceOptions,
        value: selectedInstance
          ? { label: selectedInstance.name, value: selectedInstance, key: selectedInstance.id }
          : undefined,
        onChange: (option) => {
          if (isNotNullish(option)) {
            setSelectedInstance(option.value);
          }
        },
        placeholder: 'Select an instance',
        menuPosition: 'fixed',
        isOptionDisabled: (option) => disabledOption(option.value),
        formatOptionLabel: (option) => {
          if (!disabledOption(option.value)) {
            return <div>{option.label}</div>;
          }
          return (
            <Tooltip type="simple" isPortaled={false}>
              <Tooltip.Trigger>
                <div>{option.label}</div>
              </Tooltip.Trigger>
              <Tooltip.Content>{`This instance already has a ${logType} log forwarding configuration`}</Tooltip.Content>
            </Tooltip>
          );
        },
      }}
      helpText="Log forwarding can be configured for AuraDB Business Critical instances only"
      htmlAttributes={{
        'data-testid': 'log-forwarding-instance-select',
      }}
    />
  );
};
